/**
 * "Why is this here?" cue for a product-picker row that a brand / customer
 * alias pulled in.
 *
 * The synced catalog is a read-only Sage mirror, so a family the business
 * knows by a brand name ("White Cap Canada" → the FV*NCA private-label SKUs)
 * has no description that contains the brand. The search API expands such
 * phrases through `product_search_aliases` and reports the alias that fired
 * on each result (`matched_alias`). Without this badge a CSR typing
 * "white cap canada" would see "NCA 40% Silane VOC CA" rows appear with no
 * visible connection to what they typed. Renders nothing when the result
 * matched on its own text or code.
 */
import type { ProductSearchResult } from '../../services/portalProducts';

export interface BrandAliasBadgeProps {
  product: Pick<ProductSearchResult, 'sage_item_code' | 'matched_alias'>;
}

export function brandAliasTitle(product: BrandAliasBadgeProps['product']): string | null {
  const alias = product.matched_alias?.trim();
  if (!alias) return null;
  return (
    `${product.sage_item_code} is listed because your search named "${alias}", ` +
    `a brand / customer name this SKU family is sold under. The catalog description ` +
    `itself does not contain that name.`
  );
}

export default function BrandAliasBadge({ product }: BrandAliasBadgeProps) {
  const title = brandAliasTitle(product);
  if (!title) return null;
  return (
    <span
      role="note"
      data-testid="brand-alias-badge"
      title={title}
      style={{
        fontSize: '10px',
        fontWeight: 600,
        color: '#1d4ed8',
        background: 'rgba(59, 130, 246, 0.12)',
        border: '1px solid rgba(29, 78, 216, 0.3)',
        borderRadius: '3px',
        padding: '1px 5px',
        marginLeft: '6px',
        whiteSpace: 'nowrap',
      }}
    >
      ↔ {product.matched_alias}
    </span>
  );
}
